import { create } from "zustand";
import { pushStoryFlash } from "@/state/storyFlash";

/**
 * 에러·알림 토스트 큐. ApiError 가 발생하면 handler 가 여기에 넣고, ToastRail 이 소비.
 */

export type ToastKind = "info" | "success" | "warning" | "error";
export type ToastPresentation = "rail" | "banner" | "flash";

export interface ToastEntry {
    id: string;
    kind: ToastKind;
    title?: string;
    message: string;
    createdAt: number;
    ttlMs: number;
    count: number;
}

interface ToastState {
    entries: ToastEntry[];
}

interface ToastActions {
    push: (entry: Omit<ToastEntry, "id" | "createdAt" | "count">) => string;
    dismiss: (id: string) => void;
    clear: () => void;
}

const toastTimers = new Map<string, number>();

export const useToasts = create<ToastState & ToastActions>((set, get) => ({
    entries: [],

    push: (entry): string => {
        const now = Date.now();
        const fingerprint = `${entry.kind}::${entry.title ?? ""}::${entry.message}`;
        const existing = [...get().entries].reverse().find((candidate: ToastEntry) => (
            `${candidate.kind}::${candidate.title ?? ""}::${candidate.message}` === fingerprint
            && now - candidate.createdAt < 1800
        ));

        if (existing) {
            const updated: ToastEntry = {
                ...existing,
                createdAt: now,
                ttlMs: entry.ttlMs,
                count: existing.count + 1,
            };
            set(state => ({
                entries: state.entries.map(item => item.id === existing.id ? updated : item),
            }));
            const existingHandle = toastTimers.get(existing.id);
            if (existingHandle) window.clearTimeout(existingHandle);
            const handle = window.setTimeout(() => {
                get().dismiss(existing.id);
            }, updated.ttlMs);
            toastTimers.set(existing.id, handle);
            return existing.id;
        }

        const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const full: ToastEntry = { ...entry, id, createdAt: now, count: 1 };
        set(state => ({ entries: [...state.entries, full] }));
        const handle = window.setTimeout(() => {
            get().dismiss(id);
        }, full.ttlMs);
        toastTimers.set(id, handle);
        return id;
    },

    dismiss: (id) => {
        const existing = toastTimers.get(id);
        if (existing) {
            window.clearTimeout(existing);
            toastTimers.delete(id);
        }
        set(state => ({ entries: state.entries.filter(e => e.id !== id) }));
    },
    clear: () => {
        for (const handle of toastTimers.values()) window.clearTimeout(handle);
        toastTimers.clear();
        set({ entries: [] });
    },
}));

/** 편의 함수 — 외부 모듈이 hook 없이 호출하려고. */
export function pushToast(
    kind: ToastKind,
    message: string,
    opts?: {
        title?: string;
        ttlMs?: number;
        presentation?: ToastPresentation;
        glyph?: string;
        accent?: string;
    },
) {
    const presentation = opts?.presentation ?? "rail";
    if (presentation !== "rail") {
        return pushStoryFlash({
            tier: presentation === "flash" ? "high" : "mid",
            title: opts?.title ?? message,
            body: opts?.title ? message : undefined,
            glyph: opts?.glyph,
            accent: opts?.accent,
            durationMs: opts?.ttlMs,
        });
    }
    return useToasts.getState().push({
        kind,
        message,
        title: opts?.title,
        ttlMs: opts?.ttlMs ?? (kind === "error" ? 5000 : 3000),
    });
}
